import React, { useState, useEffect, useMemo } from "react";
import {
  Container,
  Row,
  Col,
  Table,
  Spinner,
  Badge,
  Alert,
  Accordion,
} from "react-bootstrap";
import { jwtDecode } from "jwt-decode";
import { getERAById } from "../afinanceapi";
import { useParams, useLocation } from "react-router-dom";
import UnallocatedERA from "../Components/ERA/UnallocatedERA.jsx";
import AllocatedERA from "../Components/ERA/AllocatedERA.jsx";
import EraClaimCard from "../Components/ERA/EraClaimCard.jsx";
import { Breadcrumb } from '../Components/Breadcrumb/Breadcrumb';

export default function BillingERA() {
  const [era, setEra] = useState(null);
  const [loading, setLoading] = useState(true);
  const [userRole, setUserRole] = useState(false);
  const [error, setError] = useState("");
  const { id } = useParams();
  const location = useLocation();
  const [openPanel, setOpenPanel] = useState('header');

  const canAccessFinance = useMemo(() => {
    try {
      const token = sessionStorage.getItem('user');
      if (!token) return false;
      const user = jwtDecode(token);
      const roles = [
        ...(Array.isArray(user?.role) ? user.role : user?.role ? [user.role] : []),
        ...(Array.isArray(user?.permissions) ? user.permissions : []),
      ];
      return roles.some(r => ['SuperAdmin', 'Finance'].includes(r));
    } catch { return false; }
  }, []);

  const breadcrumbItems = useMemo(() => {
    if (!era) return null;

    // Incoming trail from source page (per D-06)
    const incomingTrail = location.state?.breadcrumbTrail;

    // Base ancestors: either incoming trail or canonical ERA root
    const ancestors = incomingTrail?.length
      ? [...incomingTrail]
      : [{ label: 'ERA', path: '/billing?section=eras', canAccess: canAccessFinance }];

    // Accordion sub-panel chaining (per D-07 — extend trail through sub-levels)
    if (openPanel === 'allocated') {
      return [
        ...ancestors,
        { label: era.fileName, path: `/billing/era/${id}` },
        { label: 'Allocated Claims' },
      ];
    }
    if (openPanel === 'unallocated') {
      return [
        ...ancestors,
        { label: era.fileName, path: `/billing/era/${id}` },
        { label: 'Unallocated Claims' },
      ];
    }

    // Header panel (default)
    return [
      ...ancestors,
      { label: era.fileName },
    ];
  }, [era, id, openPanel, location.state, canAccessFinance]);

  const formatCurrency = (x) =>
    x || x === 0
      ? new Intl.NumberFormat("en-ZA", {
          style: "currency",
          currency: "ZAR",
          minimumFractionDigits: 2,
        }).format(x)
      : "";

  const refetchEra = React.useCallback(async () => {
    const data = await getERAById(id);
    setEra(data);
  }, [id]);

  useEffect(() => {
    async function getData() {
      try {
        setLoading(true);
        const token = sessionStorage.getItem("user");
        if (!token) throw new Error("User not authenticated");
        const user = jwtDecode(token);

        const roles = [
          ...(Array.isArray(user?.role)
            ? user.role
            : user?.role
            ? [user.role]
            : []),
          ...(Array.isArray(user?.permissions) ? user.permissions : []),
        ];

        const allowedRoles = ["Finance", "SuperAdmin"];
        const hasAllowedRole = roles.some((r) => allowedRoles.includes(r));
        setUserRole(hasAllowedRole);

        if (!hasAllowedRole) {
          setError("You do not have permission to view this page.");
          return;
        }

        const data = await getERAById(id);
        setEra(data);
      } catch (err) {
        console.error("⛔ Error fetching ERA:", err);
        setError("Failed to load ERA data.");
      } finally {
        setLoading(false);
      }
    }
    getData();
  }, [id]);

  if (loading)
    return (
      <Container className="pt-5 text-center">
        <Spinner animation="border" variant="primary" />
        <p className="mt-2 text-muted">Loading ERA details...</p>
      </Container>
    );

  if (error)
    return (
      <Container className="pt-5">
        <Alert variant="danger">{error}</Alert>
      </Container>
    );

  if (!era)
    return (
      <Container className="pt-5">
        <Alert variant="warning">ERA not found.</Alert>
      </Container>
    );

  const h = era.header || {};
  const allocatedCount = Array.isArray(era.claims) ? era.claims.length : 0;
  const unallocatedCount = Array.isArray(era.unallocated)
    ? era.unallocated.length
    : 0;
  // Newer ERAs carry an eraClaims[] array with the original line set + every
  // date-group outcome (allocated/unallocated/skipped/duplicate). Older ERAs
  // ingested before that change won't have it — fall back to the legacy
  // Allocated/Unallocated split in that case so historical records still
  // render.
  const hasEraClaims = Array.isArray(era.eraClaims) && era.eraClaims.length > 0;
  const eraClaimsCount = hasEraClaims ? era.eraClaims.length : 0;

  return (
    <Container fluid className="px-4" style={{ marginTop: "80px" }}>
      {breadcrumbItems && <Breadcrumb items={breadcrumbItems} />}
      <Row className="mb-3">
        <Col>
          <h4 className="text-primary fw-semibold mb-0">ERA Details</h4>
          <p className="text-muted mb-0 small">
            File: <span className="fw-semibold">{era.fileName}</span> • Created:{" "}
            {new Date(era.createdAt).toLocaleString()}
          </p>
        </Col>
      </Row>

      {/* Accordion: controlled mode — tracks open panel for breadcrumb */}
      <Accordion activeKey={openPanel} onSelect={(k) => setOpenPanel(k ?? openPanel)}>
        {/* --- Header Section --- */}
        <Accordion.Item
          eventKey="header"
          className="mb-3 border-0 shadow-sm"
          style={{
            "--bs-accordion-btn-bg": "#f8f9fa",
            "--bs-accordion-active-bg": "#f8f9fa",
            "--bs-accordion-btn-padding-y": "0.4rem",
          }}
        >
          <Accordion.Header>
            <span className="fw-semibold text-primary">
              ERA Header Information
            </span>
          </Accordion.Header>
          <Accordion.Body>
            <Row>
              {/* Left Column */}
              <Col md={2}>
                <Row className="mb-1">
                  <Col xs={6} className="text-primary fw-semibold">
                    ERA Number:
                  </Col>
                  <Col xs={6}>{h.MedKreditERAnbr}</Col>
                </Row>
                <Row className="mb-1">
                  <Col xs={6} className="text-primary fw-semibold">
                    ERA Date:
                  </Col>
                  <Col xs={6}>{h.ERADate}</Col>
                </Row>
              </Col>
              <Col md={2}>
                <Row className="mb-1">
                  <Col xs={6} className="text-primary fw-semibold">
                    Payment Date:
                  </Col>
                  <Col xs={6}>{h.PaymentPaidDate}</Col>
                </Row>
                <Row className="mb-1">
                  <Col xs={6} className="text-primary fw-semibold">
                    Payment Ref:
                  </Col>
                  <Col xs={6}>{h.PaymentReference}</Col>
                </Row>
              </Col>

              {/* Right Column */}
              <Col md={3}>
                <Row className="mb-1">
                  <Col xs={4} className="text-primary fw-semibold">
                    Practice:
                  </Col>
                  <Col xs={8}>
                    {h.PracticeNumber} — {h.PracticeName}
                  </Col>
                </Row>
                <Row className="mb-1">
                  <Col xs={4} className="text-primary fw-semibold">
                    Medical Aid:
                  </Col>
                  <Col xs={8}>{h.MedicalAidName}</Col>
                </Row>
              </Col>
              <Col md={4}>
                <Row className="mb-1">
                  <Col xs={4} className="text-primary fw-semibold">
                    Administrator:
                  </Col>
                  <Col xs={8}>
                    {h.MedicalAidAdmin} ({h.MedicalAidAdminCode})
                  </Col>
                </Row>
                <Row className="mb-1">
                  <Col xs={4} className="text-primary fw-semibold">
                    MA Code:
                  </Col>
                  <Col xs={8}>{h.MedicalAidCode}</Col>
                </Row>
              </Col>
            </Row>

            <hr />

            <Row className="mt-1 text-center">
              <Col md={3}>
                <p className="fw-semibold text-primary mb-1">
                  Total Paid (Provider)
                </p>
                <h6>{formatCurrency(h.TotalPaidProvider)}</h6>
              </Col>
              <Col md={3}>
                <p className="fw-semibold text-primary mb-1">
                  Total Paid (Member)
                </p>
                <h6>{formatCurrency(h.TotalPaidMember)}</h6>
              </Col>
              <Col md={3}>
                <p className="fw-semibold text-primary mb-1">Not Paid</p>
                <h6>{formatCurrency(h.TotalNotPaid)}</h6>
              </Col>
              <Col md={3}>
                <p className="fw-semibold text-primary mb-1">
                  Member Liability
                </p>
                <h6>{formatCurrency(h.TotalMemberLiability)}</h6>
              </Col>
            </Row>
          </Accordion.Body>
        </Accordion.Item>

        {/* --- ERA Claims Section (new — one card per <era:Claim> with all
             original lines and every date-group outcome). Only rendered for
             ERAs ingested after the per-era:Claim model rollout — older docs
             without eraClaims[] fall through to the legacy panels. --- */}
        {hasEraClaims && (
          <Accordion.Item
            eventKey="claims"
            className="mb-3 border-0 shadow-sm"
            style={{
              "--bs-accordion-btn-bg": "#f8f9fa",
              "--bs-accordion-active-bg": "#f8f9fa",
              "--bs-accordion-btn-padding-y": "0.4rem",
            }}
          >
            <Accordion.Header>
              <span className="fw-semibold text-primary me-2">
                ERA Claims
              </span>
              <Badge bg="primary" pill>
                {eraClaimsCount}
              </Badge>
            </Accordion.Header>
            <Accordion.Body className="p-3">
              {era.eraClaims.map((ec, i) => (
                <EraClaimCard
                  key={`${ec.schemeClaimId || i}-${i}`}
                  eraClaim={ec}
                  eraLogId={era._id}
                  onMutated={refetchEra}
                />
              ))}
            </Accordion.Body>
          </Accordion.Item>
        )}

        {/* --- Allocated Section (legacy view) --- */}
        {!hasEraClaims && (
          <Accordion.Item
            eventKey="allocated"
            className="mb-3 border-0 shadow-sm"
            style={{
              "--bs-accordion-btn-bg": "#f8f9fa",
              "--bs-accordion-active-bg": "#f8f9fa",
              "--bs-accordion-btn-padding-y": "0.4rem",
            }}
          >
            <Accordion.Header>
              <span className="fw-semibold text-primary me-2">
                Allocated Claims
              </span>
              <Badge bg="primary" pill>
                {allocatedCount}
              </Badge>
            </Accordion.Header>
            <Accordion.Body className="p-3">
              <AllocatedERA eraAllocated={era.claims} />
            </Accordion.Body>
          </Accordion.Item>
        )}

        {/* --- Unallocated Section (legacy view) --- */}
        {!hasEraClaims && (
          <Accordion.Item
            eventKey="unallocated"
            className="mb-5 border-0 shadow-sm"
            style={{
              "--bs-accordion-btn-bg": "#f8f9fa",
              "--bs-accordion-active-bg": "#f8f9fa",
              "--bs-accordion-btn-padding-y": "0.4rem",
            }}
          >
            <Accordion.Header>
              <span className="fw-semibold text-secondary me-2">
                Unallocated Claims
              </span>
              <Badge bg="secondary" pill>
                {unallocatedCount}
              </Badge>
            </Accordion.Header>
            <Accordion.Body className="p-3">
              <UnallocatedERA eraUnallocated={era.unallocated} />
            </Accordion.Body>
          </Accordion.Item>
        )}
      </Accordion>
    </Container>
  );
}
